import type { LayerSpecification, Map as MapLibreMap } from "maplibre-gl"

export const STOP_NUMBER_LAYER_ID = "maplibre-gl-directions-waypoint-number"
export const STOP_NUMBER_IMAGE_PREFIX = "stop-number-"

const PIXEL_RATIO = 2
const HEIGHT = 18 * PIXEL_RATIO
const PADDING_X = 5 * PIXEL_RATIO
const BORDER = 1.5 * PIXEL_RATIO
const FONT = `700 ${11 * PIXEL_RATIO}px Inter, system-ui, sans-serif`
const FILL = "#1d4ed8"

/**
 * The badge drawn on the browser's canvas rather than as a map label: map
 * labels need the style's glyphs, and not every style we switch to serves the
 * same fonts.
 */
const drawBadge = (label: string) => {
  const canvas = document.createElement("canvas")
  const context = canvas.getContext("2d")
  if (!context) return null
  context.font = FONT
  const width = Math.max(
    HEIGHT,
    Math.ceil(context.measureText(label).width) + 2 * PADDING_X,
  )
  canvas.width = width
  canvas.height = HEIGHT
  // Resizing the canvas resets the context
  context.font = FONT
  const radius = HEIGHT / 2
  context.beginPath()
  context.roundRect(
    BORDER / 2,
    BORDER / 2,
    width - BORDER,
    HEIGHT - BORDER,
    radius,
  )
  context.fillStyle = FILL
  context.fill()
  context.lineWidth = BORDER
  context.strokeStyle = "#ffffff"
  context.stroke()
  context.fillStyle = "#ffffff"
  context.textAlign = "center"
  context.textBaseline = "middle"
  context.fillText(label, width / 2, HEIGHT / 2 + PIXEL_RATIO / 2)
  return context.getImageData(0, 0, width, HEIGHT)
}

/** Draws the badge a pin asked for; `false` when the id is not a badge. */
export const addMissingStopNumberBadge = (
  map: MapLibreMap,
  imageId: string,
): boolean => {
  if (!imageId.startsWith(STOP_NUMBER_IMAGE_PREFIX)) return false
  if (map.hasImage(imageId)) return true
  const image = drawBadge(imageId.slice(STOP_NUMBER_IMAGE_PREFIX.length))
  if (image) map.addImage(imageId, image, { pixelRatio: PIXEL_RATIO })
  return true
}

/**
 * Number badges at the top right of the stop pins. The pins carry
 * `stopLabel` (see `stopPinLabels`); the badge images are drawn on demand
 * through the map's `styleimagemissing` event.
 */
export const stopNumberLayer = (visible: boolean): LayerSpecification => ({
  id: STOP_NUMBER_LAYER_ID,
  type: "symbol",
  source: "maplibre-gl-directions",
  layout: {
    "icon-image": ["concat", STOP_NUMBER_IMAGE_PREFIX, ["get", "stopLabel"]],
    "icon-anchor": "bottom-left",
    // The stop icons are 24 px and centred on the stop
    "icon-offset": [5, -5],
    "icon-allow-overlap": true,
    "icon-ignore-placement": true,
    visibility: visible ? "visible" : "none",
  },
  filter: [
    "all",
    ["==", ["geometry-type"], "Point"],
    ["==", ["get", "type"], "WAYPOINT"],
    ["has", "stopLabel"],
  ],
})
